import {
  DEFAULT_POWER_SLIDE_THEME,
  inkColor,
  parseHexColor,
  POWER_SLIDE_MINIMUM_CONTRAST,
  PowerSlideTheme,
  resolveCaptionAlpha,
} from "../../../models/interactions/PowerSlideTheme";
import { PowerSlideSurface } from "../../../models/interactions/PowerSlideTemplates";

export interface PowerSlideSurfaceStyle {
  /** Slide background. */
  background: string;
  /**
   * Text colour on that background - all of it. Titles, body, captions and
   * eyebrows share one colour: the surface names an ink and that is what every
   * word renders in. Hierarchy comes from size and weight instead, which is
   * both what the type ramp already encodes and the only way a theme can
   * promise legibility. A softened tint is a third colour the author never
   * picked, and one the contrast gate never checked.
   */
  ink: string;
  /**
   * Caption text, the one deliberate exception to the rule above: a tint of the
   * ink rather than the ink, because captions read as quieter than body copy by
   * convention. The tint is not fixed - it is only as deep as this background
   * can carry at the surface's own contrast bar - so it stays a softer shade of
   * the author's ink rather than becoming an unreadable colour of its own.
   */
  inkCaption: string;
  /** Rules, dots, numerals and asset borders. Never a full-bleed background. */
  accent: string;
  /** The neutral rule under a Basic title and beside a Section eyebrow. */
  divider: string;
  /** Near-neutral ground of an empty asset slot; the dashed accent signals. */
  dropZoneFill: string;
  /** Ground behind a filled image slot, seen wherever a contained image does
   *  not reach the edges. */
  slotFill: string;
  /** Chip behind a Numbered slide's numeral. */
  badgeFill: string;
  /** Fill and border for an editable text region on this surface. */
  fieldFill: string;
  fieldBorder: string;
  fieldFocusBorder: string;
}

function withAlpha(hex: string, alpha: number): string {
  const rgb = parseHexColor(hex);
  if (!rgb) return hex;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

/**
 * Everything a slide needs to paint itself, derived from the theme's three
 * surface roles plus its accent and two inks.
 *
 * Only the background, the ink choice and the accent are authored. The
 * chrome values below - dividers, drop zones, field fills - are fixed tints of
 * the chosen ink, so a new surface colour cannot produce an unreadable divider
 * or an invisible field border.
 *
 * Text is deliberately not among them. Every word on a surface renders in the
 * ink itself, which is what lets `availableInks` guarantee legibility by
 * testing a single colour: a tinted body copy would render below the ratio the
 * gate cleared, on a colour the author never chose.
 *
 * Slide colours are deliberately independent of the editor's light/dark mode:
 * a slide must look the same either way, the same rule the game/* variables
 * already follow.
 */
export function getPowerSlideSurface(
  surface: PowerSlideSurface,
  theme: PowerSlideTheme = DEFAULT_POWER_SLIDE_THEME
): PowerSlideSurfaceStyle {
  const role = theme.surfaces[surface] ?? DEFAULT_POWER_SLIDE_THEME.surfaces[surface];
  const ink = inkColor(theme, role.ink);
  const isLightInk = role.ink === "light";

  return {
    background: role.background,
    ink,
    inkCaption: withAlpha(
      ink,
      resolveCaptionAlpha(
        ink,
        role.background,
        POWER_SLIDE_MINIMUM_CONTRAST[surface]
      )
    ),
    accent: theme.accent,
    badgeFill: withAlpha(theme.accent, 0.22),
    divider: withAlpha(ink, isLightInk ? 0.35 : 0.14),
    dropZoneFill: withAlpha(ink, isLightInk ? 0.08 : 0.04),
    // The slide's own colour rather than a grey panel. That 14% tint came from
    // the Figma slot, where it sat behind a cover-filled mock and was never
    // actually seen; with contained images it became a heavy box around every
    // picture. Letting the slide show through is what the runtime renders.
    slotFill: role.background,
    fieldFill: withAlpha(ink, isLightInk ? 0.06 : 0.04),
    fieldBorder: withAlpha(ink, isLightInk ? 0.18 : 0.14),
    // On a dark ground the accent may not read against its own surface, so the
    // focus ring falls back to the ink itself.
    fieldFocusBorder: isLightInk ? ink : theme.accent,
  };
}
